import { type AgentStateMutation, defaultApplyEvents, HttpAgent, transformChunks } from '@ag-ui/client';
import { type BaseEvent, type Message, type RunAgentInput } from '@ag-ui/core';
import { from, lastValueFrom, reduce } from 'rxjs';

/**
 * Replays raw AG-UI events (as persisted in S3) through the client's event
 * processing pipeline to produce the final `Message[]`.
 *
 * The stored events use the compact chunk format (`TEXT_MESSAGE_CHUNK`,
 * `TOOL_CALL_CHUNK`, etc.) so we pipe through `transformChunks` first to
 * expand them into the granular start/content/end events that
 * `defaultApplyEvents` expects.
 */
export async function replayEventsToMessages(events: BaseEvent[]): Promise<Message[]> {
  if (events.length === 0) {
    return [];
  }

  // Throwaway agent — only used as a state container by defaultApplyEvents.
  const agent = new HttpAgent({ threadId: '', url: '' });

  const expandedEvents = from(events).pipe(transformChunks(false));

  const input: RunAgentInput = {
    context: [],
    forwardedProps: {},
    messages: [],
    runId: '',
    state: null,
    threadId: '',
    tools: [],
  };

  return lastValueFrom(
    defaultApplyEvents(input, expandedEvents, agent, []).pipe(
      reduce<AgentStateMutation, Message[]>((acc, mutation) => mutation.messages ?? acc, []),
    ),
  );
}
